/**
 * Demo Mode Composable
 * Provides a global demo mode state that widgets can use
 */

import { ref, provide, inject, isRef, computed } from 'vue'

const DEMO_MODE_KEY = Symbol('demoMode')
const DEMO_MODE_OVERRIDE_KEY = Symbol('demoModeOverride')

/**
 * Provides demo mode state to the component tree
 * @param {boolean|Ref<boolean>} initialValue - Initial demo mode state or a ref to control it
 * @returns {Object} { demoMode, trialModeOverride } - demoMode ref and override ref for refresh
 */
export function provideDemoMode(initialValue = false) {
  const demoMode = isRef(initialValue) ? initialValue : ref(initialValue)
  const trialModeOverride = ref(null)
  provide(DEMO_MODE_KEY, demoMode)
  provide(DEMO_MODE_OVERRIDE_KEY, trialModeOverride)
  return { demoMode, trialModeOverride }
}

/**
 * Injects demo mode state from parent
 * @returns {Object} Ref-like object - use .value when override is set, returns override; otherwise demoMode
 */
export function useDemoMode() {
  const demoMode = inject(DEMO_MODE_KEY)
  const override = inject(DEMO_MODE_OVERRIDE_KEY, ref(null))
  if (!demoMode) {
    console.warn('useDemoMode: Demo mode not provided. Using default false.')
    return ref(false)
  }
  return computed(() => (override?.value != null ? override.value : demoMode.value))
}
